import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
    colorVariables,
    fontFamilies,
    fontWeights,
    votColorVariables,
} from '@gumtree/ui-library/src/base/theme';

export const ModernFooterBlock = styled.footer`
    background: #f7f7fa;
    color: #0c132d;
    padding: 24px 16px 32px;
`;

export const ModernFooterNav = styled.nav`
    margin: 0 auto;
`;

export const ModernFooterSectionCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

export const ModernFooterDetails = styled.details`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;

    &[open] .modern-footer-chevron {
        transform: rotate(225deg);
    }
`;

export const ModernFooterSummary = styled.summary`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 48px;
    padding: 12px;

    &::-webkit-details-marker {
        display: none;
    }
`;

export const ModernFooterCardTitle = styled.span`
    color: #0c132d;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
`;

export const ModernFooterChevron = styled.span`
    border: solid #0c132d;
    border-width: 0 2px 2px 0;
    display: inline-block;
    height: 8px;
    margin-right: 5px;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
    width: 8px;
`;

export const ModernFooterCardContent = styled.div`
    padding: 0 12px 12px;
`;

export const ModernFooterContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const ModernFooterGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterGroupTitle = styled.h4`
    color: #56575c;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
`;

export const ModernFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterSocialLinks = styled.div`
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    max-width: 224px;
`;

export const ModernFooterSocialLink = styled.a`
    display: inline-flex;
    height: 24px;
    width: 24px;

    svg path {
        fill: #0c132d;
    }
`;

export const ModernFooterCopyright = styled.div`
    color: #81838d;
    font-size: 13px;
    letter-spacing: 0.01em;
    line-height: 20px;

    a {
        color: #1b73e8;
    }
`;

export const ModernFooterCopyrightText = styled.span`
    display: block;
`;

export const modernFooterTextLinkStyle = css`
    color: #0c132d;
    display: inline-flex;
    font-size: 13px;
    line-height: 20px;
    padding: 4px 0;
    text-decoration: underline;
`;

export const modernFooterCardLinkStyle = css`
    color: #1b73e8;
    display: block;
    font-size: 15px;
    line-height: 22px;
    padding: 6px 0;
    text-decoration: none;
`;

export const modernFooterBadgeLinkStyle = css`
    display: inline-flex;
    height: 41px;
    width: 140px;

    svg {
        height: 100%;
        width: 100%;
    }
`;

export const VotFooterBlock = styled.footer<{ $isDesktop?: boolean }>`
    /* Mobile 页脚底 #09090B；Figma 1040:137830 padding 24/16/32 */
    background: #09090b;
    color: ${votColorVariables.neutralLight};
    font-family: 'Readex Pro', sans-serif;
    padding: 24px 16px 32px;

    @media (min-width: 48em) {
        background: ${(props) =>
            props.$isDesktop ? votColorVariables.footerBackground : '#09090b'};
        padding: 24px 32px;
    }
`;

export const VotFooterNav = styled.nav<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    /* Figma footer-info：Apps / Community / Legal 间距 32px */
    gap: 32px;
    margin: 0 auto;
    max-width: ${(props) => (props.$isDesktop ? '1350px' : '1122.645px')};

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                gap: 10px;
            `}
    }
`;

export const VotFooterPrimary = styled.div<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    /* Figma：accordion ↔ Mobile Apps = 16px */
    gap: 16px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: flex-start;
                      flex-direction: row;
                      gap: 28px;
                      height: 196px;
                      justify-content: space-between;
                  `
                : css`
                      align-items: flex-start;
                      flex-direction: row;
                      gap: 28px;
                      justify-content: space-between;
                  `}
    }
`;

export const VotFooterSectionCards = styled.div<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    /* Figma 761:67564 accordion-group：三项间距 12px */
    gap: 12px;

    @media (min-width: 48em) {
        flex: 1;
        flex-direction: row;
        gap: 16px;
    }
`;

export const VotFooterDetails = styled.details<{ $isDesktop?: boolean }>`
    /* Figma 8637:180114：展开链接在卡片外；卡片样式只落在 summary */
    background: transparent;
    border-radius: 0;
    padding: 0;

    & > summary {
        background: ${votColorVariables.footerBackground};
        border-radius: 8px;
        padding: 12px;
    }

    &[open] .vot-footer-chevron::before {
        transform: rotate(225deg);
    }

    /* Figma：一行两个、间距 12、左右 12px；超长字母打点 */
    ${ModernFooterCardContent} {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        margin-top: 12px;
        padding: 0 12px;
    }

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-self: stretch;
                      background: transparent;
                      border-radius: 0;
                      display: flex;
                      flex: 1;
                      flex-direction: column;
                      justify-content: flex-start;
                      padding: 0;

                      & > summary {
                          background: transparent;
                          border-radius: 0;
                          padding: 0;
                      }

                      ${ModernFooterCardContent} {
                          display: block;
                          gap: 0;
                          margin-top: 0;
                          padding: 6px 0 0;
                      }
                  `
                : css`
                      flex: 1;
                  `}
    }
`;

export const VotFooterSummary = styled.summary<{ $isDesktop?: boolean }>`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 24px;
    padding: 0;

    &::-webkit-details-marker {
        display: none;
    }

    @media (min-width: 48em) {
        min-height: 22px;
        padding: 0;
        pointer-events: none;
    }
`;

export const VotFooterTitle = styled.span<{ $isDesktop?: boolean }>`
    /* Figma 761:67565 H8-14/semibold */
    color: ${colorVariables.backgroundNeutral};
    font-family: ${fontFamilies.sanFranciscoText};
    font-size: 14px;
    font-weight: ${fontWeights.bold};
    line-height: 22px;

    @media (min-width: 48em) {
        font-size: ${(props) => (props.$isDesktop ? '16px' : '14px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.lightBold : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '24px' : '22px')};
    }
`;

export const VotFooterChevron = styled.span`
    align-items: center;
    display: inline-flex;
    flex: 0 0 24px;
    height: 24px;
    justify-content: center;
    width: 24px;

    &::before {
        border: solid ${colorVariables.backgroundNeutral};
        border-width: 0 1.5px 1.5px 0;
        content: '';
        display: inline-block;
        height: 8px;
        transform: rotate(45deg);
        transition: transform 0.2s ease;
        width: 8px;
    }

    @media (min-width: 48em) {
        display: none;
    }
`;

export const VotFooterApps = styled.section<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: flex-start;
                      flex: 0 0 165px;
                      justify-content: flex-start;
                  `
                : css`
                      flex: 0 0 165px;
                  `}
    }
`;

export const VotFooterGroupTitle = styled.h4<{ $isDesktop?: boolean }>`
    /* Mobile Apps 与 Figma H8-14/semibold 对齐；Desktop 保持 16/24 */
    color: ${colorVariables.backgroundNeutral};
    font-family: ${fontFamilies.sanFranciscoText};
    font-size: 14px;
    font-weight: ${fontWeights.bold};
    line-height: 22px;
    margin: 0;

    @media (min-width: 48em) {
        font-size: ${(props) => (props.$isDesktop ? '16px' : '14px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.lightBold : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '24px' : '22px')};
    }
`;

export const VotFooterCommunityTitle = styled.h4<{ $isDesktop?: boolean }>`
    /* 设计 token: --color-text-inverted-1 → 白字；Figma 761:67597 为 16/24 semibold */
    color: ${colorVariables.backgroundNeutral};
    font-size: 16px;
    font-weight: ${fontWeights.bold};
    line-height: 24px;
    margin: 0;

    @media (min-width: 48em) {
        font-size: ${(props) => (props.$isDesktop ? '14px' : '16px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.medium : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '22px' : '24px')};
    }
`;

export const VotFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const VotFooterDesktopAppBadge = styled.span<{
    $app: 'app-store' | 'google-play';
}>`
    align-items: center;
    border: 0.5px solid
        ${(props) =>
            props.$app === 'app-store'
                ? votColorVariables.footerDesktopAppStoreBorder
                : votColorVariables.footerDesktopLegalText};
    border-radius: 8px;
    box-sizing: border-box;
    color: ${colorVariables.backgroundNeutral};
    display: flex;
    gap: 5px;
    height: 32px;
    padding: 4px 16px;
    width: 122px;

    > svg {
        flex: 0 0 auto;
        height: ${(props) => (props.$app === 'app-store' ? '16px' : '17px')};
        width: ${(props) => (props.$app === 'app-store' ? '14px' : '16px')};
    }
`;

export const VotFooterDesktopAppBadgeText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    line-height: 1;
    white-space: nowrap;

    > span {
        font-size: 6.5px;
        font-weight: ${fontWeights.medium};
        letter-spacing: -0.0929px;
    }

    > strong {
        font-family: Inter, sans-serif;
        font-size: 12px;
        font-weight: ${fontWeights.lightBold};
        line-height: 16px;
    }
`;

export const VotFooterDesktopDivider = styled.div<{ $isDesktop?: boolean }>`
    display: none;

    @media (min-width: 48em) {
        display: block;
        ${(props) =>
            props.$isDesktop
                ? css`
                      background: ${votColorVariables.footerDesktopDivider};
                      height: 1px;
                      margin: 0 auto;
                      max-width: 1350px;
                      width: 100%;
                  `
                : css`
                      background: ${votColorVariables.footerDivider};
                      height: 2px;
                      margin: 16px 0;
                  `}
    }
`;

export const VotFooterSecondary = styled.div<{ $isDesktop?: boolean; hasPartners: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: center;
                      height: 100px;
                      justify-content: center;
                      padding: 0 100px;
                      position: relative;
                  `
                : css`
                      align-items: flex-start;
                      flex-direction: row;
                      justify-content: ${props.hasPartners ? 'space-between' : 'center'};
                  `}
    }
`;

export const VotFooterCommunity = styled.section<{ $isDesktop?: boolean }>`
    /* Figma 761:67597：标题+图标左对齐，无上下边框 */
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: center;
                      gap: 16px;
                      width: 100%;
                  `
                : css`
                      align-items: flex-start;
                      gap: 12px;
                      width: 100%;
                  `}
    }
`;

export const VotFooterSocialLinks = styled.div<{ $isDesktop?: boolean }>`
    align-items: center;
    display: flex;
    /* Figma 752:45877 / 761:67597：图标间距 16px，跟随标题左对齐 */
    gap: 16px;
    justify-content: flex-start;
    padding: 0;

    @media (min-width: 48em) {
        justify-content: ${(props) => (props.$isDesktop ? 'center' : 'flex-start')};
    }
`;

export const VotFooterSocialLink = styled.a<{ $isDesktop?: boolean }>`
    display: inline-flex;
    /* Figma 752:45877：社交图标 32×32 */
    height: 32px;
    width: 32px;

    svg {
        display: block;
        height: 32px;
        margin: auto;
        width: 32px;
    }

    svg path {
        fill: ${colorVariables.backgroundNeutral};
    }

    ${(props) =>
        props.$isDesktop &&
        css`
            /* Desktop VIP-PC=C：Community 社交 icon hover / focus-visible */
            &:hover svg path,
            &:hover svg circle,
            &:focus-visible svg path,
            &:focus-visible svg circle {
                fill: var(--color-text-alpha-inverted---3, rgba(255, 255, 255, 0.68));
            }
        `}
`;

export const VotFooterPartners = styled.section<{ $isDesktop?: boolean }>`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                position: absolute;
                right: 100px;
            `}
    }
`;

export const VotFooterPartnerLink = styled.a`
    background: ${votColorVariables.textMuted};
    border-radius: 2px;
    color: ${colorVariables.backgroundNeutral};
    font-size: 14px;
    font-weight: 330;
    line-height: 22px;
    padding: 4px 8px;
    text-decoration: none;
`;

export const VotFooterCopyright = styled.div<{ $isDesktop?: boolean }>`
    /* Figma 761:67625 — H9-12/regular + inverted/3，段间距 16px */
    color: ${votColorVariables.textMuted};
    display: flex;
    flex-direction: column;
    font-family: ${fontFamilies.sanFranciscoText};
    font-size: 12px;
    font-style: normal;
    font-weight: ${fontWeights.medium};
    gap: 16px;
    letter-spacing: 0;
    line-height: 18px;
    /* Nav gap 已提供与 Community 的 32px 间距 */
    margin-top: 0;

    a {
        color: ${votColorVariables.accent};
        text-decoration: none;
    }

    ${ModernFooterCopyrightText} {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    ${ModernFooterCopyrightText} > span {
        display: block;
    }

    ${ModernFooterCopyrightText} br {
        display: none;
    }

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      color: ${votColorVariables.footerDesktopLegalText};
                      display: flex;
                      flex-direction: column;
                      font-family: ${fontFamilies.sanFranciscoText};
                      font-size: 12px;
                      font-weight: ${fontWeights.medium};
                      gap: 24px;
                      line-height: 18px;
                      margin: 0;
                      max-width: none;
                      text-align: center;

                      a {
                          color: ${votColorVariables.footerDesktopLink};
                      }

                      ${ModernFooterCopyrightText} {
                          display: flex;
                          flex-direction: column;
                          gap: 20px;
                          padding: 16px 0;
                      }

                      ${ModernFooterCopyrightText} > span {
                          display: block;
                          margin: 0 auto;
                          max-width: 600px;
                      }

                      ${ModernFooterCopyrightText} br {
                          display: none;
                      }
                  `
                : css`
                      margin: 0 auto;
                      max-width: 695px;
                      text-align: center;
                  `}
    }
`;

export const VotFooterCopyrightLinks = styled.div<{ $isDesktop?: boolean }>`
    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                color: ${votColorVariables.footerDesktopLink};
                font-size: 14px;
                font-weight: ${fontWeights.medium};
                line-height: 22px;

                a {
                    color: inherit;
                    text-decoration: none;
                }
            `}
    }
`;

export const getVotFooterTextLinkStyle = (isDesktop = false) => css`
    /* Figma Mobile accordion 展开项：inverted-2 + H8-14/regular；字母级 ellipsis */
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding: 0;
    overflow: hidden;
    color: var(--color-text-inverted-2, ${votColorVariables.neutralLight});
    display: block;
    font-family: ${fontFamilies.sanFranciscoText};
    font-size: 14px;
    font-weight: ${fontWeights.medium};
    line-height: 22px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (min-width: 48em) {
        ${isDesktop
            ? css`
                  width: auto;
                  min-width: auto;
                  overflow: visible;
                  color: ${votColorVariables.footerDesktopLink};
                  font-family: 'Readex Pro', sans-serif;
                  font-size: 14px;
                  font-weight: ${fontWeights.normal};
                  line-height: 22px;
                  padding: 4px 0;
                  text-overflow: clip;
                  white-space: normal;

                  /* Desktop VIP-PC=C：三列文案链接 hover / focus-visible */
                  &:hover,
                  &:focus-visible {
                      color: var(--color-text-inverted-1, #fff);
                      font-family: 'Readex Pro', sans-serif;
                      font-size: 14px;
                      font-weight: ${fontWeights.normal};
                      line-height: 22px;
                  }
              `
            : css`
                  color: ${votColorVariables.footerLink};
                  font-family: 'Readex Pro', sans-serif;
                  font-size: 16px;
                  font-weight: ${fontWeights.normal};
                  line-height: 24px;
                  padding: 2px 0;
                  text-overflow: clip;
                  white-space: normal;
              `}
    }
`;

/** Figma Mobile「More About Our Apps」：accent + H8-14/medium */
export const getVotFooterAccentTextLinkStyle = () => css`
    color: ${votColorVariables.accent};
    display: block;
    font-family: ${fontFamilies.sanFranciscoText};
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeights.lightBold};
    line-height: 22px;
    padding: 0;
    text-decoration: none;
`;

export const getVotFooterBadgeLinkStyle = (isDesktop = false) => css`
    display: inline-flex;
    height: ${isDesktop ? '32px' : '42px'};
    width: ${isDesktop ? '122px' : '140px'};

    > svg {
        height: 100%;
        width: 100%;
    }
`;

export const votFooterBadgeLinkStyle = getVotFooterBadgeLinkStyle();
